/**
 * ErrorBoundary — ловит ошибки рендера вкладки «Оплаты», чтобы приложение НЕ уходило в
 * белый экран. Показывает аккуратный экран с кнопкой «Обновить» (сброс + повторный рендер)
 * и шлёт диагностический маячок на сервер (читаем в логах Railway; консоль браузера не нужна).
 * Данные при этом не теряются — оплата/правки уже сохранены на сервере.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

function crashBeacon(msg: string): void {
  try {
    const q = new URLSearchParams({ ev: 'app_crash', msg: msg.slice(0, 200) });
    const img = new Image();
    img.src = `/api/health/diag?${q.toString()}`;
  } catch {
    /* ignore */
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const where = (info.componentStack ?? '').split('\n').slice(0, 2).join(' ').trim();
    crashBeacon(`${error.name}: ${error.message} @ ${where}`);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="pw-state" role="alert">
          <div className="pw-state__title">Не удалось отобразить оплаты</div>
          <p className="pw-state__hint">
            Данные сохранены на сервере. Нажмите «Обновить» — если не поможет, перезагрузите карточку (⌘/Ctrl+Shift+R).
          </p>
          <button
            type="button"
            className="pw-btn pw-btn--primary"
            onClick={() => this.setState({ error: null })}
          >
            Обновить
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
